import { FormattedMessage } from 'react-intl';
import { useGetCurrentOrganization, useGetTenant } from '@/api/trafikportalen';
import { getTenantConfiguration } from '@/constants/tenantConfiguration';
import { useTenantID } from '@/hooks/useTenantID';
import { useAuth } from '@/stores/auth';
import { Section } from '../Section';
import type { StudentType } from '@/types/students';

interface StudentCategoryInfoProps {
  studentType: StudentType;
}

export const StudentCategoryInfo = ({
  studentType,
}: StudentCategoryInfoProps) => {
  const { data: tenant } = useGetTenant();
  const tenantId = useTenantID();
  const { claimType, schoolCardsArePaid } = getTenantConfiguration(tenantId);
  const isSingle = claimType === 'single';
  const { userProfile } = useAuth();
  const isOrgUser = !!userProfile?.organization_id;
  const { data: organization, isSuccess: organizationLoaded } =
    useGetCurrentOrganization({
      query: { enabled: isOrgUser },
    });
  const orgManagesPeriods = organization?.selfmanaged_school_claims === true;
  const periodEditingDisabled =
    isOrgUser && organizationLoaded && !orgManagesPeriods;

  return (
    <>
      <Section>
        <h2 className="mb-4 text-lg font-semibold text-text-900">
          <FormattedMessage defaultMessage="Kom igång" />
        </h2>
        <p className="mb-4 text-text-700">
          <FormattedMessage defaultMessage="Här är en översikt av de steg som behövs för att få igång biljettutskick till personerna i denna kategori." />
        </p>
        <ol className="flex list-decimal flex-col gap-3 pl-5 text-text-800">
          <li>
            <span className="font-medium">
              {periodEditingDisabled ? (
                <FormattedMessage defaultMessage="Kontrollera perioden" />
              ) : (
                <FormattedMessage defaultMessage="Skapa en period" />
              )}
            </span>
            <p className="mt-1 text-text-700">
              {periodEditingDisabled ? (
                <FormattedMessage defaultMessage="Under fliken Perioder ser man biljettperioderna med start- och slutdatum. Perioderna styr när biljetter beställs och gäller för personerna, men skapas och ändras av systemadministratörerna." />
              ) : (
                <FormattedMessage defaultMessage="Under fliken Perioder skapar man en biljettperiod med start- och slutdatum. Perioden styr när biljetter beställs och gäller för personerna." />
              )}
            </p>
          </li>
          <li>
            <span className="font-medium">
              <FormattedMessage defaultMessage="Lägg till personer" />
            </span>
            <p className="mt-1 text-text-700">
              <FormattedMessage defaultMessage="Importera en fil med flera personer på en gång, eller en åt gången via det manuella flödet." />
            </p>
          </li>
          <li>
            <span className="font-medium">
              <FormattedMessage defaultMessage="Följ upp status" />
            </span>
            <p className="mt-1 text-text-700">
              <FormattedMessage defaultMessage="I listan med elever kan man se statusen för varje persons biljettbeställning. Systemet bearbetar beställningarna automatiskt baserat på aktiv period." />
            </p>
          </li>
          <li>
            <span className="font-medium">
              <FormattedMessage defaultMessage="Fakturering" />
            </span>
            <p className="mt-1 text-text-700">
              <FormattedMessage defaultMessage="Fakturering sker automatiskt. Ange unik faktureringsinformation om speciella inställningar önskas." />
            </p>
          </li>
        </ol>
      </Section>

      <Section className="gap-4">
        <h2 className="text-lg font-semibold text-text-900">
          <FormattedMessage defaultMessage="Bra att veta" />
        </h2>
        <h3 className="text-base font-semibold text-text-900">
          <FormattedMessage defaultMessage="Import" />
        </h3>
        <ul className="flex list-disc flex-col gap-2 pl-5 text-text-800">
          <li>
            <FormattedMessage defaultMessage="Om man vill inaktivera alla personer går det att ladda upp en helt tom fil (måste inte finnas med en påhittad person som i gamla systemet)." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Vad som händer om ett personnummer läggs till som redan finns i systemet på..." />
            <ul className="flex list-disc flex-col gap-1 pl-5 pt-1 text-text-800">
              <li>
                <FormattedMessage defaultMessage="Samma avdelning/elevkategori: personen uppdateras med nya datan." />
              </li>
              <li>
                <FormattedMessage defaultMessage="Annan avdelning/elevkategori inom samma organisation: personen finns nu som medlem i båda avdelningarna/elevkategorierna." />
              </li>
              <li>
                <FormattedMessage defaultMessage="Annan organisation där personen finns som aktiv: inget händer. Det är inte tillåtet för samma personnummer att finnas i flera organisationer." />
              </li>
              <li>
                <FormattedMessage defaultMessage="Annan organisation där personen finns som inaktiv: personen flyttas helt från den andra organisationen, inklusive existerande skolkort." />
              </li>
            </ul>
          </li>
          <li>
            <FormattedMessage defaultMessage="Personer som har fått sin elevkategori-unika data redigerad manuellt markeras särskilt och kan komma att skrivas över vid nästa import om inte samma ändringar finns i importfilen. Efter att en import har körts tas markeringen på alla manuellt redigerade personer bort. Tänk på att namn, personnummer och e-post inte är unik per avdelning och därmed inte blir markerat om ändrat." />
          </li>
          <li>
            <FormattedMessage
              defaultMessage="Personer som finns i systemet men inte i en importerad fil kommer att markeras som inaktiva. Det går även att manuellt sätta personer som inaktiva. Inaktiva personer får inga biljetter eller kort beställda (men lagda kortbeställningar slutförs). Efter {days} dagar blir inaktiva personer permanent raderade och anonymiserade."
              values={{ days: tenant?.anonymization_interval_days ?? 60 }}
            />
          </li>
          <li>
            <FormattedMessage defaultMessage="Om en person finns i flera elevkategorier samtidigt, men där hen är inaktiv i ena, så kommer anonymisering ske per kategori." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Så fort en person blivit importerad har de möjlighet att nyttja självbetjäningen, även om de inte har fått en biljett eller ens ett kort än. Portalen skickar inte ut några e-postmeddelanden till personer som blivit importerade, så vill man informera om självbetjäningen så måste man göra det själv." />
          </li>
        </ul>
        <h3 className="text-base font-semibold text-text-900">
          <FormattedMessage defaultMessage="Biljetter" />
        </h3>
        <ul className="flex list-disc flex-col gap-2 pl-5 text-text-800">
          <li>
            <FormattedMessage defaultMessage="Om en biljett inte längre är giltig (t.ex. för att den inte blev nyttjad i tid) för en aktiv period kommer systemet automatiskt köpa en ny biljett för personen." />
          </li>
          {studentType !== 'child-and-youth' && (
            <li>
              <FormattedMessage defaultMessage="I en relationsbunden period söks biljetten fram i det externa systemet Etis utifrån vårdnadshavarnas och skolans adresser eller hållplatser. Etis hittar bara hållplatser inom 3 km från en adress, så ligger närmaste hållplats längre bort behöver en hållplats anges på personen." />
            </li>
          )}
          {!periodEditingDisabled && (
            <li>
              <FormattedMessage defaultMessage="Eftersom det kan ta lång tid för biljetter (och kort) att levereras när det är högt tryck är det starkt rekommenderat att skapa perioder som börjar minst en vecka innan biljetterna blir giltiga. På så sätt får personerna sina kort och biljetter i tid." />
            </li>
          )}
          <li>
            {periodEditingDisabled ? (
              <FormattedMessage defaultMessage="En aktiv period måste finnas för att biljetter ska beställas till personerna. Om ingen aktiv eller schemalagd period finns markeras Perioder-fliken med en notifiering. Organisationen kan se perioderna men inte skapa, ändra eller ta bort dem." />
            ) : (
              <FormattedMessage defaultMessage="En aktiv period måste finnas för att biljetter ska beställas till personerna. Om ingen aktiv eller schemalagd period finns markeras Perioder-fliken med en notifiering. Kontakta en systemadministratör om detta händer." />
            )}
          </li>
          <li>
            <FormattedMessage defaultMessage="Personerna får inte biljetter direkt (även om det finns en aktiv period) utan det schemaläggs och fixas automatiskt inom kort (går vanligtvis snabbt men vid högt tryck kan det potentiellt ta några dagar)." />
          </li>
        </ul>
        <h3 className="text-base font-semibold text-text-900">
          <FormattedMessage defaultMessage="Kort" />
        </h3>
        <ul className="flex list-disc flex-col gap-2 pl-5 text-text-800">
          <li>
            <FormattedMessage defaultMessage="Kort beställs automatiskt när det finns en aktiv period. T.ex. om det inte finns någon aktiv period under sommarlovet kommer inga kort beställas om nya personer läggs till. Har personen redan minst en bärare så kommer det inte beställas ett nytt kort. " />
          </li>
          <li>
            <FormattedMessage defaultMessage="Systemet beställer bara ett kort per person, även om det skulle finnas flera perioder aktiva samtidigt (personer som blivit anonymiserade och läggs till igen får däremot ett nytt kort). Tappar man bort sitt kort får man själv byta bärare vid självbetjäningen eller be en administratör om hjälp." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Om personer bara har en bärare kommer de få biljetter på den bäraren. Har de flera bärare kommer de få biljetter på den bäraren som är Föredragen. Vilken bärare som är Föredragen kan resenären själv ändra genom självbetjäningen eller när en administratör byter åt dem." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Personer som blir flyttade mellan organisationer behåller sitt skolkort." />
          </li>
          <li>
            <FormattedMessage defaultMessage="När en person blir anonymiserad görs resenären i biljettsystemet anonym, vilket gör att den kan 'tas över' av ett annat konto. Så under sin tid i systemet så har personen alltid med sig sitt första beställda kort (om det inte tappas bort eller liknande), och när personen inte är kvar har hen möjlighet att fortsätta nyttja kortet och t.o.m. få in det på sitt privata konto i biljettsystemet." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Alla personer i en elevkategori måste ha ett resekort, för att garantera att de kan nyttja sina biljetter. Detta innebär att även personer som har angivit giltig 'E-post i appen / Mina sidor' kommer få ett resekort beställt." />
          </li>
        </ul>
        <h3 className="text-base font-semibold text-text-900">
          <FormattedMessage defaultMessage="Betalning" />
        </h3>
        <ul className="flex list-disc flex-col gap-2 pl-5 text-text-800">
          {isSingle ? (
            <li>
              <FormattedMessage defaultMessage="Biljetter betalas månadsvis efter att de köpts." />{' '}
              {tenantId === 'llt' && (
                <FormattedMessage defaultMessage="Köpta biljetter blir billigare ju fler som köpts. Återställs mellan terminerna." />
              )}
            </li>
          ) : (
            <li>
              <FormattedMessage defaultMessage="Biljetter betalas baserat på hur många som varit utställda under föregående månad. Det har ingen betydelse ifall en biljett blivit nyttjad eller inte, det handlar bara om rätten att resa. Det har ingen betydelse hur stor del av månaden som biljetten var utställd utan det räknas alltid som hel månad. Om en biljett blev bytt (p.g.a. adressbyte) är det den dyraste biljetten som tas betalt." />
            </li>
          )}
          <li>
            <FormattedMessage defaultMessage="Transaktioner skapas utifrån aktiva perioder, inte utifrån om personen faktiskt har fått sin biljett. En person kan därför ha transaktioner trots att biljetten inte har kommit fram än, t.ex. för att hen väntar på att skolkortet ska levereras." />
          </li>
          {schoolCardsArePaid && (
            <li>
              <FormattedMessage defaultMessage="Betalning av beställda kort sker månadsvis." />
            </li>
          )}
          <li>
            <FormattedMessage defaultMessage="Under fliken Fakturering kan du ange faktureringsinformation för just denna kategori. Annars används organisationens information." />
          </li>
        </ul>
      </Section>
    </>
  );
};
